import { useState } from "react";
import { useQuery } from "react-query";
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  Search,
  X,
  Landmark,
  Wallet,
  TrendingUp,
  TrendingDown,
  Shield,
  PieChart,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const formatMoney = (v: number) => `${v?.toLocaleString()} FRW`;

const ACCOUNT_TYPES = [
  { key: "Asset", label: "Assets", icon: Landmark, color: "blue" },
  { key: "Liability", label: "Liabilities", icon: Wallet, color: "amber" },
  { key: "Equity", label: "Equity", icon: Shield, color: "violet" },
  { key: "Income", label: "Income", icon: TrendingUp, color: "emerald" },
  { key: "Expense", label: "Expenses", icon: TrendingDown, color: "rose" },
] as const;

const COLOR_MAP: Record<string, { bg: string; text: string; border: string; headerBg: string }> = {
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", headerBg: "bg-blue-600" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", headerBg: "bg-amber-600" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", headerBg: "bg-violet-600" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", headerBg: "bg-emerald-600" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", headerBg: "bg-rose-600" },
};

interface Account {
  id: number;
  code: string;
  name: string;
  accountType: string;
  balance: number;
  isActive: boolean;
  description: string | null;
}

interface AccountsResponse {
  results: Account[];
}

export default function ChartOfAccounts() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set());

  const handleSearch = (val: string) => {
    setSearch(val);
    const timeout = setTimeout(() => setDebouncedSearch(val), 400);
    return () => clearTimeout(timeout);
  };

  const toggleType = (type: string) => {
    setCollapsedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const expandAll = () => setCollapsedTypes(new Set());
  const collapseAll = () => setCollapsedTypes(new Set(ACCOUNT_TYPES.map((t) => t.key)));

  const { data, isLoading } = useQuery<AccountsResponse>(
    ["accounts", debouncedSearch],
    async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      const { data } = await api.get(`/accounts?${params.toString()}`);
      return data;
    }
  );

  const accounts: Account[] = data?.results || [];

  const grouped = ACCOUNT_TYPES.map((type) => ({
    ...type,
    accounts: accounts.filter((a) => a.accountType === type.key),
    total: accounts
      .filter((a) => a.accountType === type.key)
      .reduce((sum, a) => sum + (a.balance || 0), 0),
  })).filter((g) => g.accounts.length > 0 || !debouncedSearch);

  const grandTotal = grouped.reduce((sum, g) => sum + g.total, 0);

  const getStatusBadge = (isActive: boolean) =>
    isActive
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : "bg-slate-100 text-slate-500 border-slate-200";

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Chart of Accounts</h1>
            <p className="text-sm text-slate-500 mt-1">
              General ledger accounts grouped by type with current balances
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAll}
              className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <ChevronDown className="h-4 w-4 mr-1" />
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAll}
              className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <ChevronRight className="h-4 w-4 mr-1" />
              Collapse All
            </Button>
          </div>
        </div>

        {/* Search Filter */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[250px]">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Search Accounts</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by account code, name..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                />
              </div>
            </div>
            {debouncedSearch && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setSearch(""); setDebouncedSearch(""); }}
                className="h-9 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Account Groups */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : grouped.length === 0 ? (
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-20 text-center">
              <div className="p-4 bg-slate-100 rounded-full mb-4">
                <BookOpen className="h-8 w-8 text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-900">No accounts found</p>
              <p className="text-xs text-slate-500 mt-1">
                {debouncedSearch ? "Try adjusting your search" : "No accounts have been configured yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => {
              const colors = COLOR_MAP[group.color];
              const isCollapsed = collapsedTypes.has(group.key);
              const Icon = group.icon;

              return (
                <Card key={group.key} className={`border ${colors.border} shadow-sm overflow-hidden`}>
                  {/* Group Header */}
                  <button
                    onClick={() => toggleType(group.key)}
                    className={`w-full flex items-center justify-between px-5 py-4 ${colors.bg} hover:opacity-90 transition-opacity cursor-pointer`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${colors.headerBg}`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div className="text-left">
                        <h2 className={`text-sm font-bold ${colors.text}`}>{group.label}</h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {group.accounts.length} account{group.accounts.length !== 1 ? "s" : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm font-bold ${colors.text}`}>
                        {formatMoney(group.total)}
                      </span>
                      {isCollapsed ? (
                        <ChevronRight className={`h-5 w-5 ${colors.text}`} />
                      ) : (
                        <ChevronDown className={`h-5 w-5 ${colors.text}`} />
                      )}
                    </div>
                  </button>

                  {/* Account Table */}
                  {!isCollapsed && (
                    <div className="bg-white">
                      {group.accounts.length === 0 ? (
                        <div className="px-5 py-8 text-center text-xs text-slate-400">
                          No accounts in this category
                        </div>
                      ) : (
                        <>
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-emerald-600 hover:bg-emerald-600">
                                <TableHead className="text-xs font-semibold text-white w-[100px]">
                                  Code
                                </TableHead>
                                <TableHead className="text-xs font-semibold text-white">
                                  Account Name
                                </TableHead>
                                <TableHead className="text-xs font-semibold text-white">
                                  Description
                                </TableHead>
                                <TableHead className="text-xs font-semibold text-white text-right">
                                  Balance (FRW)
                                </TableHead>
                                <TableHead className="text-xs font-semibold text-white">
                                  Status
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {group.accounts.map((account) => (
                                <TableRow
                                  key={account.id}
                                  className="hover:bg-slate-50/50 border-b border-slate-100 last:border-0"
                                >
                                  <TableCell>
                                    <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                                      {account.code}
                                    </span>
                                  </TableCell>
                                  <TableCell className="text-sm font-medium text-slate-900">
                                    {account.name}
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-500 max-w-[300px] truncate">
                                    {account.description || "—"}
                                  </TableCell>
                                  <TableCell className="text-sm font-semibold text-slate-900 text-right">
                                    {formatMoney(account.balance)}
                                  </TableCell>
                                  <TableCell>
                                    <Badge
                                      variant="outline"
                                      className={`text-xs font-medium border ${getStatusBadge(account.isActive)}`}
                                    >
                                      {account.isActive ? "Active" : "Inactive"}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {/* Group Total */}
                          <div className={`flex items-center justify-between px-5 py-3 ${colors.bg} border-t ${colors.border}`}>
                            <span className={`text-xs font-bold uppercase tracking-wide ${colors.text}`}>
                              Total {group.label}
                            </span>
                            <span className={`text-sm font-bold ${colors.text}`}>
                              {formatMoney(group.total)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}

            {/* Grand Total */}
            <Card className="border-2 border-slate-300 shadow-sm bg-white">
              <CardContent className="flex items-center justify-between px-5 py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-600">
                    <PieChart className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">Grand Total</h3>
                    <p className="text-xs text-slate-500">
                      {grouped.reduce((sum, g) => sum + g.accounts.length, 0)} accounts across {grouped.length} types
                    </p>
                  </div>
                </div>
                <span className="text-lg font-bold text-emerald-600">
                  {formatMoney(grandTotal)}
                </span>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
